import React, { useMemo } from 'react';
import {
  Modal,
  Row,
  Col,
  Alert,
  Descriptions,
  Button,
  message,
  Spin,
  TimePicker,
  InputNumber,
  Space,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import SITELINKS from 'constants/sitelinks';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Card from 'components/card';
import { SelectField } from 'components/Forms';
import AmountInput from 'components/amountInput';
import { useMarketplaces } from 'services/MarketplaceService';
import { useStoresByMarketplace, Store } from 'services/StoreService';
import {
  CollectionPoint,
  useAddShift,
  useCreateCollectionPoint,
  useDeleteShift,
  useUpdateCollectionPoint,
  useUpdateShift,
} from 'services/CollectionsV2Service';
import defaultMarkerIcon from '../collections/mapMarkerIcon';

const { Text } = Typography;

const TIME_FORMAT = 'HH:mm';

// dayjs sin el plugin customParseFormat ignora el segundo argumento, así que
// "14:00" se parsea como fecha inválida. La armamos a mano sobre el día de hoy:
// al picker solo le importa la hora.
const parseTime = (value: string | null) => {
  if (!value) return null;
  const [hour, minute] = value.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

// Un turno en el formulario. Sin id es uno nuevo; con id, uno del punto.
interface ShiftRow {
  id?: number;
  startTime: string | null;
  estimatedDurationMinutes: number;
}

interface FormValues {
  marketplaceId: number | null;
  marketplaceName: string;
  storeId: number | null;
  storeName: string;
  address: string;
  lat: number | null;
  lng: number | null;
  amount: number | null;
  shifts: ShiftRow[];
}

const validationSchema = Yup.object().shape({
  marketplaceId: Yup.number().nullable().required('Elegí un marketplace'),
  storeId: Yup.number().nullable().required('Elegí una tienda'),
  shifts: Yup.array()
    .of(
      Yup.object().shape({
        startTime: Yup.string().nullable().required('Falta la hora'),
        estimatedDurationMinutes: Yup.number()
          .typeError('La duración tiene que ser un número')
          .moreThan(0, 'La duración tiene que ser mayor a cero'),
      }),
    )
    .min(1, 'Cargá al menos un turno'),
});

const hasCoordinates = (store?: Store) =>
  store?.lat !== null &&
  store?.lat !== undefined &&
  store?.lng !== null &&
  store?.lng !== undefined;

interface ContentProps {
  onClose: () => void;
  isSaving: boolean;
  isEdit: boolean;
}

const FormContent: React.FC<ContentProps> = ({
  onClose,
  isSaving,
  isEdit,
}: ContentProps) => {
  const { values, setFieldValue, submitForm, errors } =
    useFormikContext<FormValues>();
  const history = useHistory();

  // Las colectas de esta tienda, en En tránsito o en Histórico, ya filtradas.
  const goToStoreCollections = (base: string) => {
    const params = new URLSearchParams();
    if (values.marketplaceId)
      params.set('marketplaceId', String(values.marketplaceId));
    params.set('storeId', String(values.storeId));
    history.push(`${base}?${params}`);
  };
  const { data: marketplaces = [], isLoading: marketplacesLoading } =
    useMarketplaces();
  const { data: stores = [], isLoading: storesLoading } =
    useStoresByMarketplace(values.marketplaceId ?? undefined);

  const marketplaceOptions = useMemo(
    () => marketplaces.map((m) => ({ label: m.name, value: m.id })),
    [marketplaces],
  );
  const storeOptions = useMemo(
    () => stores.map((s: Store) => ({ label: s.name, value: s.id })),
    [stores],
  );

  const selectedStore = stores.find((s: Store) => s.id === values.storeId);
  const coordinatesOk = selectedStore
    ? hasCoordinates(selectedStore)
    : values.lat !== null && values.lng !== null;

  const filterOption = (input: string, option: any) =>
    (option?.children ?? '').toLowerCase().includes(input.toLowerCase());

  const setShift = (index: number, patch: Partial<ShiftRow>) => {
    const next = values.shifts.map((shift, i) =>
      i === index ? { ...shift, ...patch } : shift,
    );
    setFieldValue('shifts', next);
  };

  const shiftsError =
    typeof errors.shifts === 'string' ? errors.shifts : undefined;
  const duplicated = new Set(
    values.shifts
      .map((s) => s.startTime)
      .filter((t, i, all) => t && all.indexOf(t) !== i),
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Spin spinning={marketplacesLoading}>
          <SelectField
            label="Marketplace"
            fieldName="marketplaceId"
            options={marketplaceOptions}
            showSearch
            filterOption={filterOption}
            allowClear
            disabled={isEdit}
            onChange={(value: number) => {
              setFieldValue(
                'marketplaceName',
                marketplaces.find((m) => m.id === value)?.name ?? '',
              );
              setFieldValue('storeId', null);
              setFieldValue('storeName', '');
              setFieldValue('address', '');
              setFieldValue('lat', null);
              setFieldValue('lng', null);
            }}
          />
        </Spin>
      </Col>

      <Col span={24}>
        <Spin spinning={storesLoading}>
          <SelectField
            label="Tienda"
            fieldName="storeId"
            options={storeOptions}
            showSearch
            filterOption={filterOption}
            allowClear
            disabled={!values.marketplaceId || isEdit}
            onChange={(value: number) => {
              const store = stores.find((s2: Store) => s2.id === value);
              setFieldValue('storeName', store?.name ?? '');
              setFieldValue('address', store?.address ?? '');
              setFieldValue('lat', store?.lat ?? null);
              setFieldValue('lng', store?.lng ?? null);
            }}
          />
        </Spin>
      </Col>

      {(selectedStore || values.storeId) && (
        <>
          <Col span={24}>
            <Alert
              type={coordinatesOk ? 'success' : 'error'}
              showIcon
              message={
                coordinatesOk
                  ? 'La tienda tiene coordenadas cargadas'
                  : 'La tienda no tiene coordenadas'
              }
              description={
                coordinatesOk
                  ? undefined
                  : 'Sin coordenadas la colecta no se puede rutear. Cargalas en la tienda y volvé.'
              }
            />
          </Col>
          <Col span={24}>
            <Card title="Información de la tienda">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Dirección">
                      {values.address || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Código">
                      {selectedStore?.storeCode ?? '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                {/* El mapa en vez del par de coordenadas: los números alcanzaban
                    para saber que la tienda tiene ubicación, no para darse cuenta
                    de que está mal. La clave lleva las coordenadas para que al
                    cambiar de tienda el mapa se remonte: MapContainer ignora un
                    center nuevo. */}
                {coordinatesOk && (
                  <Col xs={24} md={12}>
                    <div style={{ height: 160 }}>
                      <MapContainer
                        key={`${values.lat},${values.lng}`}
                        center={[values.lat as number, values.lng as number]}
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker
                          position={[
                            values.lat as number,
                            values.lng as number,
                          ]}
                          icon={defaultMarkerIcon}
                        />
                      </MapContainer>
                    </div>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>
        </>
      )}

      <Col span={24}>
        <Space direction="vertical" size={4}>
          <span>Valor de la colecta (opcional)</span>
          <AmountInput
            value={values.amount}
            onChange={(value) => setFieldValue('amount', value)}
          />
        </Space>
      </Col>

      {/* Un punto por tienda, con sus turnos: cada uno es una hora del día y
          cuánto lleva la parada a esa hora. Al agregar el punto a una ruta se
          elige uno, o se cargan valores propios para esa colecta. */}
      <Col span={24}>
        <Card
          title="Turnos: hora de llegada y tiempo en la tienda"
          extra={
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                setFieldValue('shifts', [
                  ...values.shifts,
                  { startTime: null, estimatedDurationMinutes: 20 },
                ])
              }
            >
              Agregar turno
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Text type="secondary">
              Cada turno indica la hora de llegada y el tiempo de permanencia
              del driver en tienda.
            </Text>
            {values.shifts.length === 0 && (
              <Text type="secondary">Sin turnos. Cargá al menos uno.</Text>
            )}
            {values.shifts.map((shift, index) => (
              <Space key={shift.id ?? `new-${index}`} align="center" wrap>
                <TimePicker
                  popupClassName="hora-turno"
                  format={TIME_FORMAT}
                  minuteStep={5}
                  allowClear={false}
                  placeholder="Llega a las"
                  status={
                    !shift.startTime ||
                    (shift.startTime && duplicated.has(shift.startTime))
                      ? 'error'
                      : undefined
                  }
                  value={parseTime(shift.startTime)}
                  onChange={(value) =>
                    setShift(index, {
                      startTime: value ? value.format(TIME_FORMAT) : null,
                    })
                  }
                />
                <InputNumber
                  min={1}
                  max={240}
                  addonBefore="se queda"
                  addonAfter="min"
                  value={shift.estimatedDurationMinutes}
                  onChange={(value) =>
                    setShift(index, { estimatedDurationMinutes: value ?? 1 })
                  }
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    setFieldValue(
                      'shifts',
                      values.shifts.filter((_s, i) => i !== index),
                    )
                  }
                />
              </Space>
            ))}
            {duplicated.size > 0 && (
              <Text type="danger">Hay dos turnos a la misma hora.</Text>
            )}
            {shiftsError && <Text type="danger">{shiftsError}</Text>}
          </Space>
        </Card>
      </Col>

      <Col span={24} style={{ textAlign: 'right' }}>
        {/* Las colectas vivas de esta tienda, en En tránsito ya filtrado. Solo
            para un punto que existe: uno nuevo todavía no tiene colectas. */}
        {isEdit && values.storeId && (
          <Space style={{ float: 'left' }}>
            <Button
              icon={<UnorderedListOutlined />}
              onClick={() =>
                goToStoreCollections(SITELINKS.collections.transit)
              }
            >
              Colectas en curso
            </Button>
            <Button
              icon={<HistoryOutlined />}
              onClick={() =>
                goToStoreCollections(SITELINKS.collections.history)
              }
            >
              Histórico
            </Button>
          </Space>
        )}
        <Button onClick={onClose} style={{ marginRight: 8 }}>
          Cancelar
        </Button>
        <Button
          type="primary"
          loading={isSaving}
          disabled={!coordinatesOk || duplicated.size > 0}
          onClick={() => {
            submitForm();
          }}
        >
          {isEdit ? 'Guardar' : 'Crear'}
        </Button>
      </Col>
    </Row>
  );
};

interface Props {
  open: boolean;
  point: CollectionPoint | null;
  onClose: () => void;
}

const CollectionPointFormModal: React.FC<Props> = ({
  open,
  point,
  onClose,
}: Props) => {
  const isEdit = Boolean(point);
  const { mutateAsync: createPoint, isPending: isCreating } =
    useCreateCollectionPoint();
  const { mutateAsync: updatePoint, isPending: isUpdating } =
    useUpdateCollectionPoint();
  const { mutateAsync: addShift, isPending: isAddingShift } = useAddShift();
  const { mutateAsync: updateShift, isPending: isUpdatingShift } =
    useUpdateShift();
  const { mutateAsync: deleteShift, isPending: isDeletingShift } =
    useDeleteShift();

  const initialValues: FormValues = {
    marketplaceId: point?.marketplaceId ?? null,
    marketplaceName: point?.marketplaceName ?? '',
    storeId: point?.storeId ?? null,
    storeName: point?.storeName ?? '',
    address: point?.address ?? '',
    lat: point?.lat ?? null,
    lng: point?.lng ?? null,
    amount: point?.amount ?? null,
    shifts: (point?.shifts ?? []).map((shift) => ({ ...shift })),
  };

  // En la edición, los turnos van por sus endpoints: lo nuevo se suma, lo que
  // cambió se edita y lo que ya no está se borra. El punto no cambia de
  // tienda: un punto por tienda, y las colectas ya creadas copiaron el lugar.
  const syncShifts = async (pointId: number, shifts: ShiftRow[]) => {
    const before = point?.shifts ?? [];
    const keptIds = new Set(shifts.filter((s) => s.id).map((s) => s.id));
    // Primero lo que se borra: libera la hora para un turno nuevo a la misma.
    await Promise.all(
      before
        .filter((old) => !keptIds.has(old.id))
        .map((old) => deleteShift({ pointId, shiftId: old.id })),
    );
    await Promise.all(
      shifts.map((shift) => {
        const payload = {
          startTime: shift.startTime as string,
          estimatedDurationMinutes: Number(shift.estimatedDurationMinutes),
        };
        if (!shift.id) return addShift({ pointId, ...payload });
        const old = before.find((s) => s.id === shift.id);
        const changed =
          old &&
          (old.startTime !== payload.startTime ||
            old.estimatedDurationMinutes !== payload.estimatedDurationMinutes);
        return changed
          ? updateShift({ pointId, shiftId: shift.id, ...payload })
          : Promise.resolve(null);
      }),
    );
  };

  return (
    <Modal
      title={isEdit ? 'Editar Punto de Colecta' : 'Nuevo Punto de Colecta'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          try {
            if (isEdit && point) {
              await updatePoint({
                id: point.id,
                address: values.address,
                lat: values.lat as number,
                lng: values.lng as number,
                amount: values.amount,
              } as any);
              await syncShifts(point.id, values.shifts);
              message.success('Punto de colecta actualizado');
            } else {
              await createPoint({
                storeId: values.storeId as number,
                storeName: values.storeName,
                marketplaceId: values.marketplaceId as number,
                marketplaceName: values.marketplaceName,
                address: values.address,
                lat: values.lat as number,
                lng: values.lng as number,
                amount: values.amount,
                shifts: values.shifts.map((shift) => ({
                  startTime: shift.startTime as string,
                  estimatedDurationMinutes: Number(
                    shift.estimatedDurationMinutes,
                  ),
                })),
              });
              message.success('Punto de colecta creado');
            }
            onClose();
          } catch (error: any) {
            message.error(error?.message ?? 'No se pudo guardar el punto');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <FormContent
          onClose={onClose}
          isSaving={
            isCreating ||
            isUpdating ||
            isAddingShift ||
            isUpdatingShift ||
            isDeletingShift
          }
          isEdit={isEdit}
        />
      </Formik>
    </Modal>
  );
};

export default CollectionPointFormModal;
